// Workflow node-level memory/skill bindings — React Query hooks (DEV-16815/16816).
//
// Finer-grained than agent-level AgentMemoryBinding/SkillBinding: lets one
// node in a workflow graph see a memory/skill other nodes in the same
// workflow don't. Backed by:
//
//   GET    /v1/workflows/:workflowId/nodes/:nodeKey/memory-bindings
//   POST   /v1/workflows/:workflowId/nodes/:nodeKey/memory-bindings
//   DELETE /v1/workflows/:workflowId/nodes/:nodeKey/memory-bindings/:memoryId
//   (same shape for /skill-bindings, plus POST .../rename-bindings)
//
// Keyed by nodeKey (the stable logical-node identity), not the underlying
// WorkflowNode row id — see WorkflowNodeBindingService in apps/api.
import { useClient } from '@cortex/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface IWorkflowNodeMemoryBinding {
  workflowId: string;
  nodeKey: string;
  memoryId: string;
  orgId: string;
  createdAt: string;
}

export interface IWorkflowNodeSkillBinding {
  workflowId: string;
  nodeKey: string;
  skillId: string;
  orgId: string;
  createdAt: string;
}

const key = (workflowId: string, nodeKey: string) =>
  ['workflow-node-bindings', workflowId, nodeKey] as const;

/**
 * Memory and skill bindings are two separate REST resources with the same
 * list/add/remove shape — this factory generates one entity kind's hook
 * trio so the two families (memory/skill) can't silently drift apart (e.g.
 * one getting the unsaved-node `enabled` guard and the other not).
 */
function makeNodeBindingHooks<TBinding, TIdKey extends string>(
  resource: 'memory-bindings' | 'skill-bindings',
  idKey: TIdKey,
) {
  const cacheTag = resource === 'memory-bindings' ? 'memory' : 'skill';

  function useList(workflowId: string, nodeKey: string, options?: { enabled?: boolean }) {
    const client = useClient();
    return useQuery({
      queryKey: [...key(workflowId, nodeKey), cacheTag],
      queryFn: () =>
        client.http
          .get(`v1/workflows/${workflowId}/nodes/${encodeURIComponent(nodeKey)}/${resource}`)
          .json<TBinding[]>(),
      enabled: Boolean(workflowId && nodeKey) && (options?.enabled ?? true),
    });
  }

  function useAdd(workflowId: string, nodeKey: string) {
    const client = useClient();
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: string) =>
        client.http
          .post(`v1/workflows/${workflowId}/nodes/${encodeURIComponent(nodeKey)}/${resource}`, {
            json: { [idKey]: id },
          })
          .json<TBinding>(),
      onSuccess: async () => {
        await qc.invalidateQueries({ queryKey: [...key(workflowId, nodeKey), cacheTag] });
      },
    });
  }

  function useRemove(workflowId: string, nodeKey: string) {
    const client = useClient();
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: string) =>
        client.http.delete(
          `v1/workflows/${workflowId}/nodes/${encodeURIComponent(nodeKey)}/${resource}/${id}`,
        ),
      onSuccess: async () => {
        await qc.invalidateQueries({ queryKey: [...key(workflowId, nodeKey), cacheTag] });
      },
    });
  }

  return { useList, useAdd, useRemove };
}

const memoryBindingHooks = makeNodeBindingHooks<IWorkflowNodeMemoryBinding, 'memoryId'>(
  'memory-bindings',
  'memoryId',
);
const skillBindingHooks = makeNodeBindingHooks<IWorkflowNodeSkillBinding, 'skillId'>(
  'skill-bindings',
  'skillId',
);

/**
 * `options.enabled` should be `false` while the node hasn't been saved to
 * the workflow's current version yet — the backend 404s (`assertWorkflowAndNode`)
 * for a nodeKey that isn't in `currentVersion.nodes`, which otherwise shows
 * as a hard query error on a freshly-added, unsaved node.
 */
export const useWorkflowNodeMemoryBindings = memoryBindingHooks.useList;
export const useAddWorkflowNodeMemoryBinding = memoryBindingHooks.useAdd;
export const useRemoveWorkflowNodeMemoryBinding = memoryBindingHooks.useRemove;

export const useWorkflowNodeSkillBindings = skillBindingHooks.useList;
export const useAddWorkflowNodeSkillBinding = skillBindingHooks.useAdd;
export const useRemoveWorkflowNodeSkillBinding = skillBindingHooks.useRemove;

/**
 * Migrates a node's bindings after an in-editor rename has been saved (so
 * the new key already exists in `currentVersion.nodes`). See
 * `WorkflowNodeBindingService.renameNodeBindings` for why this is needed —
 * bindings are keyed by nodeKey, which is otherwise free to change without
 * telling either binding table.
 */
export function useRenameWorkflowNodeBindings(workflowId: string) {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ oldNodeKey, newNodeKey }: { oldNodeKey: string; newNodeKey: string }) =>
      client.http.post(
        `v1/workflows/${workflowId}/nodes/${encodeURIComponent(oldNodeKey)}/rename-bindings`,
        { json: { newNodeKey } },
      ),
    onSuccess: async (_data, { oldNodeKey, newNodeKey }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: key(workflowId, oldNodeKey) }),
        qc.invalidateQueries({ queryKey: key(workflowId, newNodeKey) }),
      ]);
    },
  });
}

/**
 * Catalog of every bindable `llm-agent` node across the org's current workflow
 * versions (DEV-17157) — powers the node-target option in the memory/skill
 * scope pickers. Backed by `GET /v1/workflows/bindable-nodes`.
 */
export interface IBindableWorkflowNode {
  workflowId: string;
  workflowName: string;
  nodeKey: string;
}

export function useBindableWorkflowNodes(options?: { enabled?: boolean }) {
  const client = useClient();
  return useQuery({
    queryKey: ['bindable-workflow-nodes'] as const,
    queryFn: () => client.http.get('v1/workflows/bindable-nodes').json<IBindableWorkflowNode[]>(),
    enabled: options?.enabled ?? true,
  });
}
